"use client";

import { CHART_COLORS } from "@/lib/tool-kit/palette";
import {
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import { niceTicks } from "@/lib/tool-kit/stats-descriptive/ticks";

const WIDTH = 640;
const HEIGHT = 300;
const PAD_LEFT = 52;
const PAD_RIGHT = 20;
const TOP = 16;
const AXIS_Y = 236;

export interface RankLine {
  label: string;
  /** Threshold at each rank 1…k. */
  values: readonly number[];
  color: string;
  dash?: string;
}

/**
 * Sorted p-values against their rank, with the rejection threshold each method applies at
 * that rank. A p-value on or below a line is rejected by that method (step procedures
 * reject up to the last rank where this holds). Lines differ in colour *and* dash, and the
 * legend names each, so colour alone never carries the meaning.
 */
export function RankPlot({
  sorted,
  lines,
  fmt,
  label,
  rankLabel,
  pLabel,
  pointsLabel,
}: {
  sorted: readonly number[];
  lines: readonly RankLine[];
  fmt: NumberFormatter;
  label: string;
  rankLabel: string;
  pLabel: string;
  pointsLabel: string;
}) {
  const k = sorted.length;
  const top = Math.max(...sorted, ...lines.flatMap((line) => [...line.values]));
  const yAxis = niceTicks(0, Math.min(1, top), 5);
  const x = (rank: number) =>
    PAD_LEFT +
    (k === 1 ? 0.5 : (rank - 1) / (k - 1)) *
      (WIDTH - PAD_LEFT - PAD_RIGHT - 20) +
    10;
  const y = (value: number) =>
    AXIS_Y - (Math.min(value, yAxis.hi) / (yAxis.hi || 1)) * (AXIS_Y - TOP);
  const legend = [
    { label: pointsLabel, color: CHART_COLORS[0], dash: undefined },
    ...lines,
  ];

  return (
    <svg
      aria-label={label}
      className="h-auto w-full text-muted-foreground"
      role="img"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
    >
      {yAxis.ticks.map((tick) => (
        <g key={tick}>
          <line
            opacity={0.2}
            stroke="currentColor"
            x1={PAD_LEFT}
            x2={WIDTH - PAD_RIGHT}
            y1={y(tick)}
            y2={y(tick)}
          />
          <text
            fill="currentColor"
            fontSize={11}
            textAnchor="end"
            x={PAD_LEFT - 6}
            y={y(tick) + 4}
          >
            {formatStat(fmt, tick)}
          </text>
        </g>
      ))}
      {lines.map((line) => (
        <polyline
          fill="none"
          key={line.label}
          points={line.values
            .map((value, i) => `${x(i + 1)},${y(value)}`)
            .join(" ")}
          stroke={line.color}
          strokeDasharray={line.dash}
          strokeWidth={2}
        />
      ))}
      {sorted.map((value, i) => (
        <circle
          cx={x(i + 1)}
          cy={y(value)}
          fill={CHART_COLORS[0]}
          // Ranks are unique, so the rank is the key.
          key={`rank-${i + 1}`}
          r={4.5}
          stroke="currentColor"
          strokeWidth={0.8}
        />
      ))}
      <line
        stroke="currentColor"
        x1={PAD_LEFT}
        x2={WIDTH - PAD_RIGHT}
        y1={AXIS_Y}
        y2={AXIS_Y}
      />
      {sorted.map((_, i) =>
        k <= 12 || i % Math.ceil(k / 12) === 0 ? (
          <text
            fill="currentColor"
            fontSize={11}
            key={`tick-${i + 1}`}
            textAnchor="middle"
            x={x(i + 1)}
            y={AXIS_Y + 16}
          >
            {fmt(i + 1)}
          </text>
        ) : null
      )}
      <text
        fill="currentColor"
        fontSize={11}
        textAnchor="middle"
        x={(PAD_LEFT + WIDTH - PAD_RIGHT) / 2}
        y={AXIS_Y + 32}
      >
        {rankLabel}
      </text>
      <text
        fill="currentColor"
        fontSize={11}
        textAnchor="middle"
        transform={`rotate(-90 12 ${(TOP + AXIS_Y) / 2})`}
        x={12}
        y={(TOP + AXIS_Y) / 2}
      >
        {pLabel}
      </text>
      {legend.map((entry, i) => (
        <g key={entry.label}>
          <line
            stroke={entry.color}
            strokeDasharray={entry.dash}
            strokeWidth={2}
            x1={PAD_LEFT + (i % 2) * 280}
            x2={PAD_LEFT + (i % 2) * 280 + 26}
            y1={AXIS_Y + 50 + Math.floor(i / 2) * 16}
            y2={AXIS_Y + 50 + Math.floor(i / 2) * 16}
          />
          <text
            fill="currentColor"
            fontSize={11}
            x={PAD_LEFT + (i % 2) * 280 + 32}
            y={AXIS_Y + 54 + Math.floor(i / 2) * 16}
          >
            {entry.label}
          </text>
        </g>
      ))}
    </svg>
  );
}
